import { DecimalPipe } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { FeatureCollection, Polygon as PolygonGJ } from 'geojson';
import { View } from 'ol';
import Feature from 'ol/Feature';
import OLMap from 'ol/Map';
import GeoJSON from 'ol/format/GeoJSON';
import MVT from 'ol/format/MVT';
import {
  LineString,
  LinearRing,
  MultiLineString,
  MultiPoint,
  MultiPolygon,
  Point,
} from 'ol/geom';
import Polygon from 'ol/geom/Polygon';
import VectorLayer from 'ol/layer/Vector';
import type { Options as VectorTileLayerOptions } from 'ol/layer/VectorTile';
import VectorTileLayer from 'ol/layer/VectorTile';
import VectorSource from 'ol/source/Vector';
import VectorTileSource from 'ol/source/VectorTile';
import { Fill, Stroke, Style, Text } from 'ol/style';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  ReplaySubject,
  combineLatest,
  of,
} from 'rxjs';
import { first, map, switchMap, take } from 'rxjs/operators';
import { LayersService } from 'src/app/core/services/layers/layers.service';
import { ProjectsFacade } from 'src/app/core/store/projects/projects.facade';
import { Geometry } from 'src/app/core/types/geometry.type';
import { generateDefaultStyle } from 'src/app/shared/icons/handleIcons';
import { environment } from 'src/environments/environment';
import { Camada } from '../../../core/types/camada.types';
import { Projeto } from '../../../core/types/projeto.types';
import {
  SearchIcadResult,
  SearchIcadResultType,
  SearchResult,
} from '../../search-bar/types/search-address-result.type';
import { wktToFeature } from '../utils/map.utils';
import { MapLegendData } from '../utils/pdf/fns';
import { generatePDF } from '../utils/pdf/pdf';
import { rgbaToHex } from '../utils/pdf/utils';
import { SnackBarService } from './../../../core/layout/snackbar.service';

declare const jsts;

const ZOOM_MULTIPLIER = 100000;

const INCHES_PER_UNIT: Record<string, number> = { m: 39.37, dd: 4374754 };
const DOTS_PER_INCH = 72;
function scaleFromResolution(resolution: number, units: 'm' | 'dd' = 'm') {
  return Math.round(INCHES_PER_UNIT[units] * DOTS_PER_INCH * resolution);
}

export type LaudoType = 'laudo' | 'pecastecnicas' | 'croquis' | 'previa' | 'avaliacao' | 'laudo_salas' | 'laudo-sala-comercial' | 'laudo-apartamento';

export interface LayerMap {
  id?: number;
  name?: string;
  active?: boolean;
  opened?: boolean;
  showGeo?: boolean;
  visibilidade?: boolean;
  cor?: string;
  espessura?: number;
  preenchimento?: string;
  tipoLinha?: string;
  simboloPonto?: number;
  tamanhoPonto?: number;
  type?: string;
  conteudo?: string;
  rotuloAtivo?: boolean;
  rotuloNome?: string;
  representacao?: string;
  equacao?: string;
  rotuloId?: number;
  tamanhoRotulo?: number;
  legenda?: any[];
  mapaTematico?: any[];
  sldBody?: string;
  sldBodyOuv?: string;
  nomeArquivo?: string;
  idCampoMapaTematico?: number;
  campoMapaTematico?: string;
  layerType?: string;
  ordemVisualizacao?: number;
  qtdGeometrias?: number;
  minZoom?: number;
  maxZoom?: number;
  url?: string;
  params?: string;
  tipoImagem?: string;
  bbox?: any;
  showOuv?: boolean;
  publicoOuv?: boolean;
  selectable?: boolean;
  additionalLayer?: boolean;
}

export const ICAD_LAYER_NAME = {
  LOTES: 'Lotes',
  EDIFICACOES: 'Edificações',
  LOGRADOUROS: 'Logradouros',
  PREAMAR: 'Preamar',
  APP: 'APP',
};

export interface ImovelPessoa {
  id: number;
  nome: string;
  cpf: string;
}

export interface Imovel {
  id: number;
  area: number;
  inscrição_imobiliária: string;
  insc_lote: string;
  tipo: string;
  id_edificacao: number;
  idIntegracao?: number;
  m2: number;
  loteamentos: {
    id_integracao: null;
    id_loteamentos: number;
    nome: string;
  };
  logradouros: {
    caixa: number;
    canteiro_central: number;
    cep: string;
    id_integracao: number;
    id_logradourous: number;
    nome: string;
    passeio: number;
    pista: number;
    sistema_viario: string;
  };
  numero: string;
  quadra: string;
  lote: string;
  imposto: 'PREDIAL' | 'TERRITORIAL';
  pessoa: ImovelPessoa | null;
  pessoas: ImovelPessoa | null;
}

interface ImoveisResponse {
  next: string | null;
  previous: string | null;
  results: Imovel[];
  count: number;
}

interface LotesStyle {
  fill: string;
  strokeColor: string;
  strokeWeight: number;
}

export interface ImovelCaracteristica {
  pk: number;
  img: null | string;
  id_caracteristicas: {
    pk: number;
    descricao: string;
    categoria: 'Imóvel' | 'Terreno' | 'Edificação';
  };
  id_caracteristicas_opcoes: {
    pk: number;
    descricao: string;
  } | null;
  valor: number | null;
}

export type ImovelCaracteristicasResponse = ImovelCaracteristica[];

export interface RelatorioV2Params {
  city: string;
  params: {
    lote_id?: number;
    imobiliario_id?: number;
    [key: string]: any;
  };
  type: 'laudo' | 'pecastecnicas' | 'croquis' | 'previa' | 'avaliacao';
  query?: object;
}

// first cap group is insc_lote
const inscRegexp = () =>
  /^((?:[0-9]*\.){3}[0-9]*)(?:(?:\.[0-9]*\.[0-9]*)|(?:\.[0-9]*))?$/;

@Injectable({
  providedIn: 'root',
})
export class IcadService {
  apiAddr$ = new ReplaySubject(1);
  private zones: any;

  // jsts
  private jstsParserOL3 = new jsts.io.OL3Parser();
  private jstsReaderWKT = new jsts.io.WKTReader();
  private jstsReaderGeoJSON = new jsts.io.GeoJSONReader();

  private lotes: FeatureCollection<PolygonGJ, any>;
  private layers: Camada[];
  private lotesStyle: LotesStyle;
  // searchResultsSubj$ is used to share data between the search-bar
  // and the list. The reason why this subject isn't used directly by
  // findByTerm is because the search-bar may have more search results
  // than the ones provided by findByTerm, e.g. results from the address
  // API. That way, search-bar interacts with this subject by calling
  // the pushSearchResults method, and the list uses the searchResults$
  // observable to get the results.
  private searchResultsSubj$ = new BehaviorSubject<SearchResult[] | null>(null);
  searchResults$ = this.searchResultsSubj$.asObservable();

  private renderer: Renderer2;

  constructor(
    private http: HttpClient,
    private layersService: LayersService,
    private projectsFacade: ProjectsFacade,
    private decimalPipe: DecimalPipe,
    private snackBarService: SnackBarService,
    rendererFactory: RendererFactory2
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  generateVectorLayerForConsultaPrevia(layerMap: any) {
    const source = new VectorTileSource({
      url: `${environment.apiUrl}vt/{z}/{x}/{y}?camada=${layerMap.id}`,
      format: new MVT(),
      // usar cache padrão do OL para não rebaixar tiles a cada movimento
      // cacheSize default ~128; remover override agressivo
      // cacheSize: 128,
      maxZoom: 21,
      tileLoadFunction: (tile: any, url) => {
        tile.setLoader(async (extent, resolution, projection) => {
          const data = await this.layersService.getVectorTiles(url);
          const format = tile.getFormat();
          const features = format.readFeatures(data, {
            extent,
            featureProjection: projection,
          });
          tile.setFeatures(features);
        });
      },
    });

    const layerOptions: VectorTileLayerOptions = {
      source,
      className: `${layerMap.name}-${layerMap.id}`,
      zIndex: 1,
      renderMode: 'hybrid',
    };

    const layer = new VectorTileLayer(layerOptions);

    if (layerMap?.estiloOuv) {
      const layerStyle = JSON.parse(layerMap.estiloOuv as string);

      const escalaMax = layerStyle?.escalaMax ?? layerStyle?.labelMaxScale ?? 0;
      const escalaMin =
        layerStyle?.escalaMin ?? layerStyle?.labelMinScale ?? 2500000;

      if (typeof layer.setMinZoom === 'function') {
        layer.setMinZoom((+escalaMax || 0) / ZOOM_MULTIPLIER);
        layer.setMaxZoom((+escalaMin || 2500000) / ZOOM_MULTIPLIER);
      } else {
        (layer as any).set('minZoom', (+escalaMax || 0) / ZOOM_MULTIPLIER);
        (layer as any).set('maxZoom', (+escalaMin || 2500000) / ZOOM_MULTIPLIER);
      }

      const styleFunction = this.generateLayerStyle(layerStyle);
      layer.setStyle(styleFunction);
    }

    return layer;
  }

  generateLayerStyle(layerStyle: any) {
    const defaultStyle = generateDefaultStyle(layerStyle);

    const styleFunction = (feature: Feature<any>, resolution: number) => {
      defaultStyle.setText(undefined as any);

      if (layerStyle.labelActive) {
        const currentScale = scaleFromResolution(resolution, 'm');
        const minScale = +layerStyle.labelMinScale || 2500000;
        const maxScale = +layerStyle.labelMaxScale || 0;
        const withinScale = currentScale >= maxScale && currentScale <= minScale;

        if (withinScale) {
          const text = this.generateTextLabel(feature, layerStyle);
          if (text) defaultStyle.setText(text);
        }
      }

      return defaultStyle;
    };

    return styleFunction;
  }

  generateTextLabel(feature: Feature<any>, layerStyle: any) {
    const fontSize = layerStyle.labelSize || 12;
    const fontType = layerStyle.labelFont || 'Arial';
    const label = layerStyle.labelActive
      ? isNaN(feature.get('rotulo'))
        ? feature.get('rotulo')
        : parseInt(feature.get('rotulo'), 10).toFixed(0)
      : '';

    const placement =
      feature.getGeometry().getType() === 'LineString' ? 'line' : 'point';

    return new Text({
      font: `700 ${fontSize}px "${fontType}"`,
      text: label,
      fill: new Fill({ color: layerStyle.labelColor || '#000' }),
      stroke: new Stroke({
        color: layerStyle.labelShadowColor || '#fff',
        width: layerStyle.labelShadowSize || 3,
      }),
      placement,
    });
  }

  private async isWithinLayer(
    layerID: number,
    selectedFeatureJSTS: any
  ): Promise<boolean> {
    const geos = (
      (await this.layersService
        .getAllFeaturesFromLayer(layerID)
        .pipe(first())
        .toPromise()) as {
        id: number;
        seq: number;
        geomText: string;
      }[]
    ).map(({ geomText }) => this.jstsReaderWKT.read(geomText));

    for (const geo of geos) {
      if (geo.intersects(selectedFeatureJSTS.geometry)) {
        return true;
      }
    }

    return false;
  }

  // apiAddr is expected to end with a slash.
  init(apiAddr: string) {
    // proj4.defs(
    //   'EPSG:31982',
    //   '+proj=utm +zone=22 +south +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs'
    // );
    // proj4.defs('EPSG:4326', '+proj=longlat +datum=WGS84 +no_defs');
    // register(proj4);

    this.jstsParserOL3.inject(
      Point,
      LineString,
      LinearRing,
      Polygon,
      MultiPoint,
      MultiLineString,
      MultiPolygon
    );

    this.http.get(`${apiAddr}perm/`).subscribe(() => {
      this.apiAddr$.next(apiAddr);
    });
  }

  setLotes(lotes: FeatureCollection<PolygonGJ, any>) {
    this.lotes = lotes;
  }

  setLotesStyle(style: LotesStyle) {
    this.lotesStyle = style;
  }

  getLotesStyle() {
    return this.lotesStyle;
  }

  loadImoveis(inscLote: string) {
    return this.apiAddr$.pipe(
      take(1),
      switchMap((apiAddr: string) => {
        const isRincao = apiAddr.includes('rincao');
        const hasPoint = inscLote?.split('.')?.length > 1 && isRincao;
        return this.http.get<ImoveisResponse>(
          `${apiAddr}imoveis/?${
            isRincao ? 'inscrição_imobiliária__istartswith' : 'insc_lote'
          }=${inscLote}${hasPoint ? '.' : ''}`
        );
      }),
      map(({ results }) => results)
    );
  }

  loadZones() {
    return this.apiAddr$.pipe(
      take(1),
      switchMap((apiAddr) => this.http.get(`${apiAddr}zonasNovas/`))
    );
  }

  loadLayers() {
    return this.projectsFacade.icadId$.pipe(
      take(1),
      switchMap((icadId) =>
        this.http.get<Projeto>(`${environment.apiUrl}projetos/${icadId}`)
      ),
      map(({ projetosCamadas }) => projetosCamadas)
    );
  }
  getLayerByID(layerID: number) {
    return this.layersService.getLayerById(layerID);
  }

  getLogradouroInfo(idIntegracaoLogradouro: any) {
    return this.projectsFacade.icadId$.pipe(
      take(1),
      switchMap((icadId) => {
        const url = `${environment.apiUrl}consulta-geral/logradouros?idProjeto=${icadId}&q=${idIntegracaoLogradouro}`;
        return this.http.get<any[]>(url);
      })
    );
  }

  getGeometryValues(id: string) {
    return this.http.get(`${environment.apiUrl}valores/geometria/${+id}`);
  }

  getImovelCaracteristicas(imovelID: any) {
    return this.apiAddr$.pipe(
      take(1),
      switchMap((apiAddr) =>
        this.http.get<ImovelCaracteristicasResponse>(
          `${apiAddr}imoveis/${imovelID}/caracteristicas/`
        )
      )
    );
  }

  getBlumenauAPIToken() {
    return this.http.get<{ token: string }>(
      `${environment.apiUrl}proxy-v2/blumenau/auth`
    );
  }

  async generatePDF(selectedFeature: any, imovel: Imovel) {
    // map target
    const el = document.createElement('div');
    el.id = 'olMap';
    el.className = 'ol-map';
    el.style.width = '715px';
    el.style.height = '300px';
    el.style.position = 'fixed';
    el.style.top = '-300px';
    el.style.zIndex = '1000';
    document.body.appendChild(el);

    const selectedFeatureOLGeoJSON = new GeoJSON().readFeature(
      selectedFeature,
      {
        dataProjection: 'EPSG:4326',
        featureProjection: 'EPSG:3857',
      }
    );
    const selectedGeometryOL = (selectedFeatureOLGeoJSON as any).getGeometry();
    // selectedFeatureJSTS is in the 4326 proj.
    const selectedFeatureJSTS = this.jstsReaderGeoJSON.read(selectedFeature);

    // zones
    if (!this.zones) {
      this.zones = await this.loadZones().pipe(take(1)).toPromise();
    }

    const zonesSource = new VectorSource({
      features: new GeoJSON().readFeatures(this.zones),
    });

    let zoneProps: any;
    for (const zf of zonesSource.getFeatures()) {
      const geo = zf.getGeometry();
      if (geo) {
        const geoJSTS = this.jstsParserOL3.read(geo);
        if (geoJSTS.intersects(selectedFeatureJSTS.geometry)) {
          zoneProps = zf.getProperties();
          break;
        }
      }
    }

    // layers
    if (!this.layers) {
      this.layers = (await this.loadLayers()
        .pipe(take(1))
        .toPromise()) as Camada[];
    }

    const legendItems: MapLegendData = [];
    const tileLayers: VectorTileLayer[] = [];

    // logradouros
    const logrsLayer = this.layers.find(
      ({ nome }) => nome === ICAD_LAYER_NAME.LOGRADOUROS
    );
    if (logrsLayer) {
      const layer = this.generateVectorLayerForConsultaPrevia(logrsLayer);

      tileLayers.push(layer);

      legendItems.push({
        label: 'VIAS',
        size: 'small',
        fillColor: rgbaToHex(logrsLayer.cor || '') || 'black',
      });
    }

    // GET LOGRADOURO INFO
    const lastLogradouroGeom = (
      (await this.getLogradouroInfo((imovel.logradouros as any).idIntegracao)
        .pipe(take(1))
        .toPromise()) as any[]
    ).pop();

    let logrsCaixa = 0;
    let logrsCantCent = 0;
    let logrsPass = 0;
    let logrsPista = 0;
    let logrsSisViar = '';

    if (!lastLogradouroGeom) {
      this.snackBarService.open(
        {
          type: 'info',
          message:
            'Não foi possível pegar as informações do logradouro. Contate o setor de cadastro',
        },
        { duration: 5000 }
      );
    }

    if (lastLogradouroGeom) {
      const logradouroValues = await this.getGeometryValues(
        lastLogradouroGeom.id
      )
        .pipe(take(1))
        .toPromise();

      if (logradouroValues) {
        const values = logradouroValues[0].valores;
        // const logrsNome =
        //   logradouroValues.find((v) => v.campoView.nome === 'nome').valorText || '';
        logrsCaixa =
          values.find((v) => v.campoView.nome === 'CAIXA').valorNum || 0;
        logrsCantCent =
          values.find((v) => v.campoView.nome === 'CANTEI_CEN').valorNum || 0;
        logrsPass =
          values.find((v) => v.campoView.nome === 'PASSEIO').valorNum || 0;
        logrsPista =
          values.find((v) => v.campoView.nome === 'PISTA').valorNum || 0;
        logrsSisViar =
          values.find((v) => v.campoView.nome === 'SISTEMA_VI').valorText || '';
      }

      if (isNaN(logrsCaixa)) {
        logrsCaixa = 0;
      }

      if (isNaN(logrsCantCent)) {
        logrsCaixa = 0;
      }

      if (isNaN(logrsPass)) {
        logrsCaixa = 0;
      }

      if (isNaN(logrsPista)) {
        logrsCaixa = 0;
      }
    }
    // -- GET LOGRADOURO INFO

    // lotes
    const lotesLayer = this.layers.find(
      ({ representacao }) => representacao === ICAD_LAYER_NAME.LOTES
    );
    if (lotesLayer) {
      const layer = this.generateVectorLayerForConsultaPrevia(lotesLayer);

      tileLayers.push(layer);

      legendItems.push({
        label: 'LOTES',
        borderColor: rgbaToHex(lotesLayer.cor || '') || 'black',
      });
    }

    // edificações
    const edificLayer = this.layers.find(
      ({ representacao }) => representacao === ICAD_LAYER_NAME.EDIFICACOES
    );
    if (edificLayer) {
      const layer = this.generateVectorLayerForConsultaPrevia(edificLayer);

      tileLayers.push(layer);

      legendItems.push({
        label: 'CONSTRUÇÕES',
        borderColor: rgbaToHex(edificLayer.cor || '') || 'black',
      });
    }

    // preamar
    let isWithinPreamar = false;
    const preamarLayer = this.layers.find(
      ({ nome }) => nome === ICAD_LAYER_NAME.PREAMAR
    );
    if (preamarLayer) {
      isWithinPreamar = await this.isWithinLayer(
        preamarLayer.id,
        selectedFeatureJSTS
      );

      const layer = this.generateVectorLayerForConsultaPrevia(preamarLayer);

      tileLayers.push(layer);

      legendItems.push({
        label: 'ÁREA MARINHA',
        fillColor: rgbaToHex(preamarLayer.preenchimento || '') || 'black',
        // fillColor: '#414e8f',
      });
    }

    // app
    let isWithinAPP = false;
    const appLayer = this.layers.find(
      ({ nome }) => nome === ICAD_LAYER_NAME.APP
    );
    if (appLayer) {
      isWithinAPP = await this.isWithinLayer(appLayer.id, selectedFeatureJSTS);

      const layer = this.generateVectorLayerForConsultaPrevia(appLayer);

      tileLayers.push(layer);

      legendItems.push({
        label: 'APP',
        fillColor: rgbaToHex(appLayer.preenchimento || '') || 'black',
        borderColor: rgbaToHex(appLayer.cor || '') || 'black',
      });
    }

    // lotes & imóveis
    const loteValues = (
      (await this.layersService
        .getGeometryValues(selectedFeature.id)
        .pipe(take(1))
        .toPromise()) as any
    )[0];

    const imovelGeometry = imovel.id_edificacao
      ? ((await this.layersService
          .getGeometryById(imovel.id_edificacao)
          .pipe(take(1))
          .toPromise()) as Geometry)
      : undefined;

    let imovelImg: HTMLImageElement | undefined = undefined;
    let base64Image: string;
    if (
      imovelGeometry &&
      imovelGeometry.arquivos &&
      imovelGeometry.arquivos.length
    ) {
      await new Promise((resolve) => {
        imovelImg = new Image();
        imovelImg.crossOrigin = 'anonymous';
        // @ts-ignore
        imovelImg.src = imovelGeometry?.arquivos[0]?.link;
        imovelImg.onload = () => {
          base64Image = this.getBase64Image(imovelImg);
          resolve(true);
        };
      });
    }

    const imovelCaracs = await this.getImovelCaracteristicas(imovel.id)
      .pipe(take(1))
      .toPromise();

    // openlayers
    const vecSource = new VectorSource();

    vecSource.addFeature(selectedFeatureOLGeoJSON as any);

    const map = new OLMap({
      layers: [
        ...tileLayers,
        new VectorLayer({
          source: vecSource,
          zIndex: 10,
          style: new Style({
            stroke: new Stroke({
              color: 'blue',
              width: 3,
            }),
            fill: new Fill({
              color: 'rgba(0, 0, 255, 0.1)',
            }),
          }),
        }),
      ],
      pixelRatio: 1,
      view: new View({
        center: [0, 0],
        zoom: 2,
      }),
    });

    map.getView().fit(selectedGeometryOL as any, {
      maxZoom: 18.2,
    });

    map.once('rendercomplete', () => {
      const canvasEls = Array.from(
        map.getTargetElement().getElementsByTagName('canvas')
      );
      const finalCanvas = document.createElement('canvas');
      finalCanvas.width = 715;
      finalCanvas.height = 300;

      const finalCanvasCtx = finalCanvas.getContext('2d');

      if (finalCanvasCtx) {
        canvasEls.forEach((canvasEl: HTMLCanvasElement) => {
          if (canvasEl.width > 0) {
            finalCanvasCtx.drawImage(canvasEl, 0, 0);
          }
        });
      }

      const mapImgURL = finalCanvas.toDataURL();

      const reduzidoField = loteValues.valores.find(
        ({ campoView }) => campoView.nome === 'REDUZIDO'
      );
      const reduzidoFieldValue = reduzidoField
        ? reduzidoField.valorNum!.toString()
        : '';

      const distanciaField = loteValues.valores.find(
        ({ campoView }) => campoView.nome === 'DISTANCIA_'
      );
      const distanciaFieldValue =
        distanciaField && distanciaField.valorNum
          ? this.decimalPipe.transform(
              distanciaField.valorNum,
              undefined,
              'pt'
            ) + ' m'
          : '';

      const areaField = loteValues.valores.find(
        ({ campoView }) => campoView.nome === 'AREA'
      );
      const areaFieldValue = areaField ? areaField.valorNum : '';

      const areaLoteCarac = imovelCaracs?.find(
        (c) => c.id_caracteristicas.pk === 36
      );

      const areaConstruidaCarac = imovelCaracs?.find(
        (c) => c.id_caracteristicas.pk === 42
      );

      const testadaImovelCarac = imovelCaracs?.find(
        (c) => c.id_caracteristicas.pk === 37
      );

      const logrs = imovel.logradouros;
      const logrsNome = logrs ? logrs.nome : '';
      // const logrsCaixa = logrs ? logrs.caixa : 0;
      // const logrsCantCent = logrs ? logrs.canteiro_central : 0;
      // const logrsPass = logrs ? logrs.passeio : 0;
      // const logrsPista = logrs ? logrs.pista : 0;
      // const logrsSisViar = logrs ? logrs.sistema_viario : '';

      generatePDF({
        reduzido: (imovel as any).idIntegracao.toString(),
        inscMob: imovel.inscrição_imobiliária || '',
        prop: imovel.pessoas ? imovel.pessoas.nome : '',
        loteamento: imovel.loteamentos ? imovel.loteamentos.nome : '',
        quadra: imovel.quadra || '',
        lote: imovel.lote || '',
        areaConstruida: areaConstruidaCarac?.valor
          ? this.decimalPipe.transform(
              areaConstruidaCarac?.valor,
              undefined,
              'pt'
            ) + ' m²'
          : '',
        logradouro: imovel.logradouros ? imovel.logradouros.nome : '',
        numero: imovel['número'] || '',
        mapImgURL,
        areaTerreno: areaLoteCarac?.valor
          ? this.decimalPipe.transform(areaLoteCarac?.valor, undefined, 'pt') +
            ' m²'
          : '',
        testada: testadaImovelCarac
          ? this.decimalPipe.transform(
              testadaImovelCarac.valor,
              undefined,
              'pt'
            ) + ' m'
          : '',
        distEsq: distanciaFieldValue,
        rua: {
          nome: logrsNome || '',
          caixa:
            this.decimalPipe.transform(
              isNaN(logrsCaixa) ? '' : logrsCaixa,
              undefined,
              'pt'
            ) || '',
          cantCentral:
            this.decimalPipe.transform(
              isNaN(logrsCantCent) ? '' : logrsCantCent,
              undefined,
              'pt'
            ) || '',
          passeio:
            this.decimalPipe.transform(
              isNaN(logrsPass) ? '' : logrsPass,
              undefined,
              'pt'
            ) || '',
          pista:
            this.decimalPipe.transform(
              isNaN(logrsPista) ? '' : logrsPista,
              undefined,
              'pt'
            ) || '',
          sisViar: logrsSisViar || '',
        },
        zona: {
          tipo_uso: zoneProps ? zoneProps.tipo_uso : '',
          tipo_comp: zoneProps ? zoneProps.tipo_comp : '',
          indice_apr: zoneProps ? zoneProps.indice_apr : '',
          taxa_ocupa: zoneProps ? zoneProps.taxa_ocupa : '',
          num_pav: zoneProps ? zoneProps.num_pav : '',
          recuo_f_r: zoneProps ? zoneProps.recuo_f_r : '',
          recuo_f_c: zoneProps ? zoneProps.recuo_f_c : '',
          recuo_lat: zoneProps ? zoneProps.recuo_lat : '',
          recuo_fun: zoneProps ? zoneProps.recuo_fun : '',
          ind_apr_ba: zoneProps ? zoneProps.ind_apr_ba : '',
          num_pav_c: zoneProps ? zoneProps.num_pav_c : '',
          ind_apr_ma: zoneProps ? zoneProps.ind_apr_ma : '',
          recuo_f_e: zoneProps ? zoneProps.recuo_f_e : '',
          recuo_f_i: zoneProps ? zoneProps.recuo_f_i : '',
          taxa_perme: zoneProps ? zoneProps.taxa_perme : '',
        },
        mapLegend: legendItems,
        imovelImg: base64Image,
        docs: {
          isWithinAPP,
          isWithinPreamar,
        },
      });

      document.body.removeChild(el);
    });

    map.setTarget(el);
  }

  getBase64Image(img) {
    const canvas = this.renderer.createElement('canvas');
    canvas.width = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0);
    const dataURL = canvas.toDataURL('image/png');
    return dataURL.replace(/^data:image\/(png|jpg);base64,/, '');
  }

  findByTerm(name: string): Observable<SearchIcadResult[]> {
    // if (!name || !this.lotes) {
    //   return of([]);
    // }

    return this.apiAddr$.pipe(
      take(1),
      switchMap((apiAddr) =>
        this.http.get<ImoveisResponse>(`${apiAddr}imoveis/?search=${name}`)
      ),
      map(({ results }) => {
        const already = new Map<string, any>();
        const res: Observable<SearchIcadResult>[] = [];

        results.forEach((imovel) => {
          const match = inscRegexp().exec(imovel['inscrição_imobiliária']);

          if (match && !already.has(match[1])) {
            const insc = match[1];

            res.push(
              this.projectsFacade.icadId$.pipe(
                take(1),
                switchMap((icadId) =>
                  this.http
                    .get<
                      {
                        id: number;
                        geom: string;
                        valorString: string;
                      }[]
                    >(
                      `${environment.apiUrl}consulta-geral/icad?idProjeto=${icadId}&q=${insc}`
                    )
                    .pipe(
                      switchMap((r) =>
                        !r.length
                          ? EMPTY
                          : of({
                              type: SearchIcadResultType,
                              id: r[0].id,
                              insc: insc,
                              geo: wktToFeature(r[0].geom) as PolygonGJ,
                            } as SearchIcadResult)
                      )
                    )
                )
              )
            );

            already.set(insc, undefined);
          }
        });

        return res;
      }),
      switchMap((obss) => (obss.length ? combineLatest(obss) : of([])))
    );
  }

  pushSearchResults(res: SearchResult[] | null) {
    this.searchResultsSubj$.next(res);
  }

  generateReport({
    city,
    id,
    type,
    query = {},
  }: {
    city: string;
    id: number;
    type: 'laudo' | 'pecastecnicas' | 'croquis' | 'previa';
    query?: object;
  }) {
    const keys = Object.keys(query);
    const parsedData = keys.reduce((acc, curr) => {
      return (acc += curr + '=' + query[curr] + '&');
    }, `${environment.reportUrl}${city}/${id}/${type}.pdf?`);

    return this.http.get<any[]>(parsedData);
  }

  generateReportV2({
    city,
    params,
    type,
    query,
    convertToTxt = false,
  }: {
    city: string;
    params: {
      lote_id?: number;
      imobiliario_id?: number;
      idIntegracao?: number;
    };
    type: LaudoType;
    query?: object;
    convertToTxt?: boolean;
  }) {
    let body;
  
    if (convertToTxt) {
      const paramsAsText = JSON.stringify(params, null, 2);
      const file = new Blob([paramsAsText], { type: 'text/plain' });
      const formData = new FormData();
      formData.append('params', file, 'params.txt');
      body = formData; // O corpo será o formData contendo o arquivo
      
    } else {
      body = params; // Caso contrário, o corpo será o objeto params normalmente
    }

    const queryString = new URLSearchParams(query as Record<string, string>).toString();
    const url = `${environment.reportV2Url}${city}/${type}.pdf?${convertToTxt ? 'isFormData=true' : ''}${queryString || ''}`;
  
    return this.http.post(url, body, {
      responseType: 'blob',
    });
  }
  

  /* generateReportV2({
    city,
    params,
    type,
    query,
  }: {
    city: string;
    params: {
      lote_id?: number;
      imobiliario_id?: number;
      idIntegracao?: number;
    };
    type: LaudoType;
    query?: object;
  }) {
    return this.http.post(
      `${environment.reportV2Url}${city}/${type}.pdf?${query || ''}`,
      params,
      {
        responseType: 'blob',
      }
    );
  } */
}
